package ai.wagemix.logic.analytics

/**
 * The analytics event catalog (planning/analytics-plan.md §5): the one place that says which FSLog events are
 * analytics and what OpenPanel calls them.
 *
 * Every catalogued event is emitted in Kotlin as `WM<PascalCase>` (Structured Logging Extensions, 31493) and recorded
 * in OpenPanel as the snake_case name without the prefix (`WMMixSaved` → `mix_saved`; `WMScreenView` →
 * `screen_view`, OpenPanel's own page-view name). An event that is not listed here, such as the foundation's
 * `UserAction` or the data layer's `WMSyncFailure`, is a diagnostic and never reaches analytics. Adding an event to
 * the catalog is a product decision: add its branch here and its row to the plan.
 */
object WMAnalyticsCatalog {

    /**
     * The OpenPanel name of a catalogued FSLog event.
     *
     * @param eventName the FSLog event name (`WMMixSaved`)
     * @return the OpenPanel name (`mix_saved`), or null when the event is not analytics
     */
    fun openPanelNameOf(eventName: String): String? {
        return when (eventName) {
            // Screen views (site)
            "WMScreenView" -> "screen_view"
            // Account and identity
            "WMUserIdentified" -> "user_identified"
            "WMLoginSessionStarted" -> "login_session_started"
            "WMSignUpCompleted" -> "sign_up_completed"
            "WMLoginCompleted" -> "login_completed"
            "WMAuthFailed" -> "auth_failed"
            "WMPasswordResetRequested" -> "password_reset_requested"
            "WMPasswordResetCompleted" -> "password_reset_completed"
            "WMEmailVerified" -> "email_verified"
            "WMSignedOut" -> "signed_out"
            // Mix building
            "WMMixStarted" -> "mix_started"
            "WMOccupationPickerOpened" -> "occupation_picker_opened"
            "WMOccupationSearched" -> "occupation_searched"
            "WMOccupationsAdded" -> "occupations_added"
            "WMOccupationRemoved" -> "occupation_removed"
            "WMOccupationWeightChanged" -> "occupation_weight_changed"
            "WMOccupationToggled" -> "occupation_toggled"
            "WMOccupationLocked" -> "occupation_locked"
            "WMChartModeChanged" -> "chart_mode_changed"
            "WMProjectionChanged" -> "projection_changed"
            "WMFilterChanged" -> "filter_changed"
            "WMFilterEmptyResult" -> "filter_empty_result"
            "WMPremiumGateHit" -> "premium_gate_hit"
            "WMMixSaved" -> "mix_saved"
            "WMMixDiscarded" -> "mix_discarded"
            "WMMixShared" -> "mix_shared"
            "WMMixReevaluationRequested" -> "mix_reevaluation_requested"
            "WMMixReevaluationCompleted" -> "mix_reevaluation_completed"
            "WMMixReevaluationFailed" -> "mix_reevaluation_failed"
            "WMMixTutorialStepCompleted" -> "mix_tutorial_step_completed"
            "WMMixTutorialSkipped" -> "mix_tutorial_skipped"
            // Job descriptions
            "WMJobDescriptionUploadStarted" -> "job_description_upload_started"
            "WMJobDescriptionAnalyzed" -> "job_description_analyzed"
            "WMJobDescriptionFailed" -> "job_description_failed"
            "WMUploadQuotaReached" -> "upload_quota_reached"
            // Saved and shared mixes
            "WMMixOpened" -> "mix_opened"
            "WMMixDeleted" -> "mix_deleted"
            "WMSharedMixViewed" -> "shared_mix_viewed"
            "WMCtaClicked" -> "cta_clicked"
            // Billing
            // Checkout, the billing portal and subscriptions are reported by the API (analytics plan §5a).
            "WMPlanSelected" -> "plan_selected"
            else -> null
        }
    }
}
